import { RANKS, REF_SCORE } from '@dc1/shared';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentView } from '../../lib/content';
import s from './dashboard.module.css';
import { type DashMember, type Zone, zoneBands, zoneOf } from './dashboard-model';

const pct = (n: number) => `${Math.max(0, Math.min(100, n))}%`;

interface RankingProps {
  members: readonly DashMember[];
  meId: string;
  content: ContentView;
  onOpen: (memberId: string) => void;
  /** Inside the dashboard's detail sheet: compact rows, no card, the sheet's title stands for the heading. */
  bare?: boolean;
}

/**
 * "1. Xếp hạng điểm năng lực": one bar per person over the Level zones, sorted by confirmed score (API order).
 * Desktop: one-line rows grouped under a heading per zone, as design v3 draws it ("compact" density).
 */
export function Ranking({ members, meId, content, onOpen, bare }: RankingProps) {
  const { t } = useTranslation();
  const zoneName = (z: Zone) =>
    z === 'below'
      ? t('dashboard.zoneBelow', { rank: content.ranks[RANKS[0]].name })
      : t('dashboard.zoneRank', { rank: content.ranks[z].name });
  const standingText = (m: DashMember) =>
    t(
      m.standing === 'below'
        ? 'dashboard.standingBelow'
        : m.standing === 'above'
          ? 'dashboard.standingAbove'
          : 'dashboard.standingMeets',
    );
  const sub = (m: DashMember) => `${content.ranks[m.rank].name} · ${t(`roles.${m.role}`)}`;
  const zoneCounts = new Map<Zone, number>();
  for (const m of members) {
    const z = zoneOf(m.effectiveScore);
    zoneCounts.set(z, (zoneCounts.get(z) ?? 0) + 1);
  }

  return (
    <section className={s.card} data-bare={bare} aria-labelledby="dash-ranking">
      <div className={`${s.sectionHead} ${s.sectionHeadEnd} ${s.rankHead}`}>
        <div className={bare ? 'visually-hidden' : `${s.sectionTitle} ${s.sectionTitleWide}`}>
          <h2 id="dash-ranking">{t('dashboard.rankingTitle')}</h2>
          <p>{t('dashboard.rankingNote')}</p>
        </div>
        <span className={s.legend}>
          <span>
            <span className={s.swatchSolid} />
            {t('dashboard.legendConfirmed')}
          </span>
          <span>
            <span className={s.swatchHatch} />
            {t('dashboard.legendSelf')}
          </span>
        </span>
      </div>

      <div className={s.rankDesk}>
        <div className={s.rankDeskInner}>
          <div className={`${s.rankGrid} ${s.rankColumns}`}>
            <span>#</span>
            <span>{t('dashboard.member')}</span>
            <ZoneScale label={zoneName} />
            <span className={s.alignRight}>{t('dashboard.colScore')}</span>
            <span>{t('dashboard.colStanding')}</span>
          </div>
          {members.map((m, i) => {
            const zone = zoneOf(m.effectiveScore);
            const prev = members[i - 1];
            const firstInZone = !prev || zoneOf(prev.effectiveScore) !== zone;
            return (
              <Fragment key={m.id}>
                {firstInZone ? (
                  <h3 className={s.zoneHead} data-zone={zone}>
                    <span className={s.zoneSwatch} aria-hidden="true" />
                    {zoneName(zone)}
                    <span className={s.zoneCount}>
                      · {t('dashboard.zoneCount', { n: zoneCounts.get(zone) ?? 0 })}
                    </span>
                  </h3>
                ) : null}
                <button
                  type="button"
                  className={`${s.rankGrid} ${s.rankRow}`}
                  data-me={m.id === meId}
                  data-zone={zone}
                  onClick={() => {
                    onOpen(m.id);
                  }}
                >
                  <span className={s.pos}>{i + 1}</span>
                  <span className={s.who}>
                    <span className={s.name} title={m.displayName}>
                      {m.displayName}
                    </span>
                    <span className={s.sub}>{sub(m)}</span>
                  </span>
                  <ScoreTrack member={m} />
                  <span className={s.score}>
                    <b>{m.effectiveScore}</b>
                    <span className={s.scoreSelf}> / {m.selfScore}</span>
                    {/* The zone heading shows it on screen; the row repeats it for screen readers. */}
                    <span className="visually-hidden"> · {zoneName(zone)}</span>
                  </span>
                  <span className={s.standingText} data-standing={m.standing}>
                    {standingText(m)}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className={s.rankMob}>
        <div className={s.rankMobScale}>
          <ZoneScale label={(z) => (z === 'below' ? t('dashboard.zoneBelowShort') : content.ranks[z].name)} />
        </div>
        {members.map((m, i) => {
          const zone = zoneOf(m.effectiveScore);
          return (
            <button
              key={m.id}
              type="button"
              className={s.rankMobRow}
              data-zone={zone}
              onClick={() => {
                onOpen(m.id);
              }}
            >
              <span className={s.rankMobTop}>
                <span className={s.pos}>{i + 1}</span>
                <span className={s.whoText}>
                  <span className={s.name}>{m.displayName}</span>
                  <span className={s.sub}>
                    {sub(m)} · <b className={s.zoneInline}>{zoneName(zone)}</b>
                  </span>
                </span>
                <span className={s.rankMobScore}>
                  <b>{m.effectiveScore}</b>
                  <span> / {m.selfScore}</span>
                </span>
              </span>
              <span className={s.rankMobBottom}>
                <ScoreTrack member={m} />
                <span className={s.standing} data-standing={m.standing}>
                  {standingText(m)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** Zone labels, coloured bands and the Junior/Mid/Senior reference numbers above the bars. */
function ZoneScale({ label }: { label: (z: Zone) => string }) {
  const bands = zoneBands();
  return (
    <span className={s.zoneScale} aria-hidden="true">
      <span className={s.zoneLabels}>
        {bands.map((b) => (
          <span key={b.zone} data-zone={b.zone} style={{ width: pct(b.to - b.from) }}>
            {label(b.zone)}
          </span>
        ))}
      </span>
      <span className={s.zoneBands}>
        {bands.map((b) => (
          <span key={b.zone} data-zone={b.zone} style={{ width: pct(b.to - b.from) }} />
        ))}
      </span>
      {RANKS.map((r) => (
        <span key={r} className={s.zoneTick} style={{ left: pct(REF_SCORE[r]) }}>
          {REF_SCORE[r]}
        </span>
      ))}
    </span>
  );
}

/** Confirmed score solid in the zone colour, the self-assessed surplus hatched, reference lines dashed. */
function ScoreTrack({ member }: { member: DashMember }) {
  return (
    <span className={s.bar} aria-hidden="true">
      <span className={s.barTrack} />
      {RANKS.map((r) => (
        <span key={r} className={s.barRef} style={{ left: pct(REF_SCORE[r]) }} />
      ))}
      <span className={s.barFill}>
        <span className={s.barConf} style={{ width: pct(member.effectiveScore) }} />
        <span
          className={s.barSelf}
          style={{ width: pct(Math.max(0, member.selfScore - member.effectiveScore)) }}
        />
      </span>
    </span>
  );
}
